import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { DashboardData, Settings } from '@/types'

import Link from 'next/link'

import {
    ArrowLeftRight,
    ArrowRight,
    Boxes,
    CircleCheck,
    Package,
    PackagePlus,
    TriangleAlert,
    Wallet
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button, buttonVariants } from '@/components/ui/button'
import { pluralize } from '@/lib/format'
import { describeError } from '@/lib/db'
import { getDashboard, getSettings } from '@/lib/inventory/queries'
import { useSettings } from '@/components/generals/settings-provider'
import { useInventoryActions } from '@/components/inventory/inventory-actions'
import PageHeader from '@/components/generals/page-header'
import SectionCard from '@/components/generals/section-card'
import EmptyState from '@/components/generals/empty-state'
import LoadError from '@/components/generals/load-error'
import StatTile from '@/components/generals/stat-tile'
import RestockList from '@/components/inventory/restock-list'
import StockHealth from '@/components/inventory/stock-health'
import ActivityChart from '@/components/inventory/activity-chart'
import RecentMovements from '@/components/inventory/recent-movements'
import CategoryValueChart from '@/components/inventory/category-value-chart'
import { ACTIVITY_DAYS, DEFAULT_SETTINGS } from '@/constants/inventory'

interface HomeProps {
    settings: Settings
    data: DashboardData | null
}

const linkButton = buttonVariants({ variant: 'ghost', size: 'sm' })

const Dashboard: FC<{ data: DashboardData }> = ({ data }): ReactNode => {
    const { money, number } = useSettings()
    const { openNewProduct, openMovement } = useInventoryActions()
    const { summary, restock, recent, activity, categoryValues } = data

    const alerts = summary.lowCount + summary.outCount
    const okCount = Math.max(summary.productCount - alerts, 0)

    if (!summary.productCount) {
        return (
            <div className='rounded-xl border border-dashed bg-card'>
                <EmptyState
                    icon={PackagePlus}
                    title='Empieza añadiendo tus productos'
                    description='Crea tus productos con el stock que tienes hoy y un mínimo de seguridad. El panel te avisará de lo que hay que reponer y del valor de tu inventario.'
                    action={(
                        <Button onClick={openNewProduct}>
                            <PackagePlus aria-hidden='true' />
                            Nuevo producto
                        </Button>
                    )}
                    className='py-16'
                />
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-6'>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4 xl:grid-cols-4'>
                <StatTile
                    label='Productos'
                    value={number(summary.productCount)}
                    hint={`En ${number(summary.categoryCount)} ${pluralize(summary.categoryCount, 'categoría', 'categorías')}`}
                    icon={Boxes}
                    href='/inventario'
                    hrefLabel='Ver inventario'
                />

                <StatTile
                    label='Unidades en stock'
                    value={number(summary.totalUnits)}
                    hint='Suma de todos los productos'
                    icon={Package}
                />

                <StatTile
                    label='Valor del inventario'
                    value={money(summary.stockValue)}
                    hint={summary.saleValue > 0 ? <>A precio de compra. A precio de venta: <span className='font-medium text-foreground tabular-nums'>{money(summary.saleValue)}</span></> : 'A precio de compra'}
                    icon={Wallet}
                />

                <StatTile
                    label='Por reponer'
                    value={number(alerts)}
                    hint={alerts
                        ? `${number(summary.outCount)} ${pluralize(summary.outCount, 'agotado', 'agotados')} y ${number(summary.lowCount)} con stock bajo`
                        : 'Todo por encima del mínimo'}
                    icon={alerts ? TriangleAlert : CircleCheck}
                    tone={summary.outCount ? 'danger' : alerts ? 'warning' : 'success'}
                    href={alerts ? '/inventario?estado=reponer' : undefined}
                    hrefLabel={alerts ? 'Ver productos' : undefined}
                />
            </div>

            <div className='grid gap-4 lg:grid-cols-3'>
                <SectionCard
                    id='reponer'
                    title='Necesitan reposición'
                    description='Productos en su stock mínimo o por debajo, los más urgentes primero.'
                    className='lg:col-span-2'
                    action={alerts > restock.length ? (
                        <Link href='/inventario?estado=reponer' className={linkButton}>
                            Ver los {number(alerts)}
                            <ArrowRight aria-hidden='true' />
                        </Link>
                    ) : undefined}
                >
                    {restock.length ? (
                        <RestockList products={restock} />
                    ) : (
                        <EmptyState
                            icon={CircleCheck}
                            tone='success'
                            title='Todo en orden'
                            description='Ningún producto está por debajo de su stock mínimo.'
                            className='py-8'
                        />
                    )}
                </SectionCard>

                <SectionCard id='estado' title='Estado del stock' description='Cómo están tus productos respecto a su mínimo.'>
                    <StockHealth ok={okCount} low={summary.lowCount} out={summary.outCount} />
                </SectionCard>
            </div>

            <div className='grid gap-4 lg:grid-cols-3'>
                <SectionCard
                    id='actividad'
                    title='Entradas y salidas'
                    description={`Unidades movidas cada día en los últimos ${ACTIVITY_DAYS} días.`}
                    className='lg:col-span-2'
                >
                    <ActivityChart activity={activity} />
                </SectionCard>

                <SectionCard id='valor' title='Valor por categoría' description='Dónde está el dinero de tu inventario.'>
                    <CategoryValueChart categories={categoryValues} />
                </SectionCard>
            </div>

            <SectionCard
                id='ultimos-movimientos'
                title='Últimos movimientos'
                description='Lo último que ha entrado, salido o se ha ajustado.'
                action={recent.length ? (
                    <Link href='/movimientos' className={linkButton}>
                        Ver historial
                        <ArrowRight aria-hidden='true' />
                    </Link>
                ) : undefined}
            >
                {recent.length ? (
                    <RecentMovements movements={recent} />
                ) : (
                    <EmptyState
                        icon={ArrowLeftRight}
                        title='Aún no hay movimientos'
                        description='Registra la primera entrada o salida y aparecerá aquí.'
                        action={(
                            <Button variant='outline' onClick={() => openMovement()}>
                                <ArrowLeftRight aria-hidden='true' />
                                Registrar movimiento
                            </Button>
                        )}
                        className='py-8'
                    />
                )}
            </SectionCard>
        </div>
    )
}

const Home: FC<HomeProps> = ({ data }): ReactNode => {
    const { settings } = useSettings()
    const { openMovement, openNewProduct } = useInventoryActions()

    return (
        <Layout>
            <Seo />

            <div className='flex flex-col gap-6'>
                <PageHeader
                    eyebrow={(
                        <span className='inline-flex items-center gap-2 text-sm font-medium text-primary'>
                            <span className='size-1.5 rounded-full bg-primary' aria-hidden='true' />
                            {settings.id ? (
                                <span data-mdlfy-cms={`settings:${settings.id}:business_name`}>{settings.businessName}</span>
                            ) : settings.businessName}
                        </span>
                    )}
                    title='Panel de stock'
                    description='Lo importante de un vistazo: qué hay que reponer, cuánto vale tu inventario y qué se ha movido últimamente.'
                    actions={(
                        <>
                            <Button variant='outline' onClick={openNewProduct}>
                                <PackagePlus aria-hidden='true' />
                                Nuevo producto
                            </Button>

                            <Button onClick={() => openMovement()}>
                                <ArrowLeftRight aria-hidden='true' />
                                Registrar movimiento
                            </Button>
                        </>
                    )}
                />

                {data ? <Dashboard data={data} /> : <LoadError />}
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    try {
        const [settings, data] = await Promise.all([getSettings(), getDashboard()])

        return { props: { settings, data } }
    }
    catch (error) {
        console.error('[panel] No se pudo cargar el resumen:', describeError(error))

        return { props: { settings: DEFAULT_SETTINGS, data: null } }
    }
}

export default Home
